import { StoryModel } from '../models/story.model';
import { uniq } from 'lodash';
import { uuid } from '../../shared/uuid';
import { Environment } from 'src/environments/environment.model';
import { mergeSceneGraphs, updateChapterShowSelectPickThroughObjects } from './merge-scene-graph';

import { mergeColorRecipes, updateChapterColorRecipes } from './merge-color-recipes';
import { mergeReflections } from './merge-reflections';
import { mergeAssetLibraries } from './merge-asset-libraries';
import { setLightsForChapters } from './merge-lights';
import { ContentType } from '../models/content-type.enum';
import { mergeParticleSystems } from './merge-particle-systems';

export function mergeModules(
  currentStory: StoryModel,
  currentStoryId: string,
  newStory: StoryModel,
  newStoryId: string,
  exportType: Environment['exportType'] = ContentType.Bookmark,
  prefixUuidFunc: uuid.PrefixUuidFunc = uuid.idWithPrefix,
): StoryModel {
  // start by merging global properties
  const { sceneGraph, graphsMerged, objectIdsUniqueToNew, objectIdsUniqueToOriginal } =
    mergeSceneGraphs(currentStory.sceneGraph, newStory.sceneGraph);

  const assetLibraries = mergeAssetLibraries(currentStory.assetLibraries, newStory.assetLibraries);

  const reflections = mergeReflections(
    currentStory.reflections,
    newStory.reflections,
    currentStoryId,
    newStoryId,
  );

  const { colorRecipes, newColorRecipeIds } = mergeColorRecipes(
    currentStory.colorRecipes,
    newStory.colorRecipes,
    prefixUuidFunc,
  );

  // then merge chapter properties

  const currentChaptersUpdatedLights = setLightsForChapters(
    currentStory.chapters,
    currentStory.assetLibraries.lights,
  );

  const newChaptersUpdatedLights = setLightsForChapters(
    newStory.chapters,
    newStory.assetLibraries.lights,
  );

  const currentChaptersUpdatedProps = updateChapterShowSelectPickThroughObjects(
    currentChaptersUpdatedLights,
    graphsMerged,
    objectIdsUniqueToNew,
  );

  const newChaptersUpdatedProps = updateChapterShowSelectPickThroughObjects(
    newChaptersUpdatedLights,
    graphsMerged,
    objectIdsUniqueToOriginal,
  );

  // give imported chapters a new id
  const importedChaptersWithNewIds = newChaptersUpdatedProps.map((c) => {
    return { ...c, id: prefixUuidFunc('chapter') };
  });

  const mergedParticles = mergeParticleSystems(
    currentStory,
    newStory,
    currentChaptersUpdatedProps,
    importedChaptersWithNewIds,
    prefixUuidFunc,
  );

  const importedChaptersWithUpdatedColorRecipes = updateChapterColorRecipes(
    mergedParticles.importedChapters,
    newColorRecipeIds,
  );

  const chapters = [...mergedParticles.currentChapters, ...importedChaptersWithUpdatedColorRecipes];

  // TODO: what types of customData besides ParticleSystems am I throwing out from imported modules?
  const customData = {
    ...currentStory.customData,
    particleSystems: mergedParticles.mergedParticles,
  };

  const tags = exportType === 'module' ? currentStory.tags : [];

  return {
    ...currentStory,
    assetLibraries,
    chapters,
    sceneGraph,
    customData,
    tags,
    reflections,
    colorRecipes,
    stateId: uniq([...currentStory.stateId, ...newStory.stateId]),
    tweenParticleLookup: mergedParticles.tweenParticleLookup,
  };
}
